import { useMemo, useState } from 'react';
import { ButtonGreen, ButtonGrey, ButtonRed } from '../shared/Buttons';
import { Input } from '../shared/Input';
import { FormRow, InputRow } from '../shared/InputRow';
import { Modal, ModalProps } from '../shared/Modal';
import {
	checkCurrencyFormatFull,
	checkCurrencyFormatPartly,
	checkIntFormatFull,
	convertEnumsToOptions,
	convertObjectToOptions,
	getOptionList,
} from '../../scripts/scripts';
import { Select } from '../shared/Select';
import {
	TixBought,
	TixCurrencyTypes,
	TixEvent,
	TixSale,
	TixSecondaryName,
	TixSecondaryTypes,
	TixTransferNames,
	TixTransferTypes,
} from '../../types/TixTypes';
import toast from 'react-hot-toast';
import BigNumber from 'bignumber.js';
import { GETEventResponseBase } from '../../types/TixAPITypes';
import { EditSaleAPIFunc } from '../../scripts/hooks/useEditSaleAPI';
import { FlexCol, FlexRowMiddle } from '../shared/Flex';
import { Text } from '../shared/Text';
import { useApiMutation } from '../../scripts/api/api_v2';
import styled from 'styled-components';
import { secondaryWithOptionalReference } from './ModalSaledata';

const StyledSeatsContainer = styled(FlexCol)`
	row-gap: 0.5rem;
`;

const StyledSeatRow = styled(FlexRowMiddle)`
	justify-content: space-between;
`;

export type OnClickEditSaleModal = (data: { event: GETEventResponseBase; saleId: TixSale['id'] }) => void;

export function ModalEditSale({
	closeModal,
	saleId,
	event,
	editSaleAPI,
	onSaleDeleted,
}: ModalProps & {
	saleId: TixSale['id'];
	event: GETEventResponseBase;
	editSaleAPI: EditSaleAPIFunc;
	onSaleDeleted: (eventId: TixEvent['id'], orderId: TixSale['id']) => void;
}) {
	const { fetchData: deleteSaleAPI } = useApiMutation('tix.delete-sale');

	const sale = event.sales.find((sold) => sold.id === saleId)!;

	const [saving, setSaving] = useState<boolean>(false);

	const [formState, setFormState] = useState({
		tixId: sale.tixId,
		secondary: sale.secondary,
		quantity: sale.quantity,
		amount: sale.amount,
		reference: sale.reference,
		transferType: sale.transferType,
		buyerEmail: sale.buyerEmail,
		buyerName: sale.buyerName,
		amountNative: sale.amountNative,
		amountCurrency: sale.amountCurrency,
		amountReceivedDate: sale.amountReceivedDate,
		note: sale.note,
		status: sale.status,
	});

	const onChange = (name: keyof typeof formState, value: string | number) => {
		setFormState((state) => ({ ...state, [name]: value }));
	};

	const onDeleteSeat = (seatId: TixBought['id']) => {
		setFormState((state) => ({ ...state, tixId: state.tixId.filter((tixId) => tixId !== seatId) }));
	};

	const onClickSave = async () => {
		try {
			setSaving(true);
			const nativeAmountSaleBody =
				formState.amountNative && formState.amountCurrency
					? {
							amountNative: new BigNumber(formState.amountNative).toNumber(),
							amountCurrency: formState.amountCurrency as TixCurrencyTypes,
						}
					: {
							amountNative: null,
							amountCurrency: null,
						};

			const saleBody = {
				tixId: formState.tixId,
				secondary: formState.secondary as TixSecondaryTypes,
				quantity:
					formState.transferType === TixTransferTypes.FINE ? 0 : new BigNumber(formState.quantity).toNumber(),
				amount: formState.amount ? new BigNumber(formState.amount).toNumber() : null,
				reference: formState.reference,
				transferType: formState.transferType,
				status: formState.status,
				buyerEmail: formState.transferType === TixTransferTypes.FINE ? null : formState.buyerEmail || null,
				buyerName: formState.transferType === TixTransferTypes.FINE ? null : formState.buyerName || null,
				amountReceivedDate: formState.amountReceivedDate,
				note: formState.note,
				...nativeAmountSaleBody,
			} satisfies Partial<TixSale>;

			const savedSaleRes = await editSaleAPI({
				originalEvent: event,
				saleId: saleId,
				updatedSale: saleBody,
			});
			if (savedSaleRes) {
				toast.success(`Sale ${sale.reference} edited!`);
				closeModal();
			}
		} catch (err) {
			console.error(err);
		} finally {
			setSaving(false);
		}
	};

	const checkInputs = () => {
		if (!formState['secondary']) return false;
		if (!formState['transferType']) return false;
		if (formState.secondary !== TixSecondaryTypes.UNSOLD && !isFine && !formState['quantity']) return false;
		if (!optionalReference && formState.secondary !== TixSecondaryTypes.UNSOLD && !formState['reference']) {
			return false;
		}
		if (!formState['amountNative'] && formState['amountCurrency']) return false;
		if (formState['amountNative'] && !formState['amountCurrency']) return false;
		return true;
	};

	const onClickDeleteSale = async () => {
		setSaving(true);
		const isDeleted = await deleteSaleAPI({ params: { saleId: sale.id } });
		if (isDeleted?.success) {
			onSaleDeleted(event.id, sale.id);
			toast.success(`Sale ${sale.reference} deleted!`);
			closeModal();
		} else {
			toast.error(isDeleted?.error ?? 'Failed to delete sale!');
			setSaving(false);
		}
	};

	const seatInfo = useMemo(() => {
		const idSet = new Set(formState.tixId);
		const seats: TixBought[] = Object.values(event.seats).filter((b) => idSet.has(b.id));
		return seats;
	}, [formState.tixId, event.seats]);

	const isFine = (() => formState.transferType === TixTransferTypes.FINE)();
	const isUnsold = formState.secondary === TixSecondaryTypes.UNSOLD;
	const optionalReference = secondaryWithOptionalReference.includes(formState.secondary);

	return (
		<Modal title="Edit Sale" closeModal={closeModal}>
			{!isFine && (
				<FormRow>
					<StyledSeatsContainer>
						<Text $color="text_desc_main" $size="1.6rem" $weight="600">
							Selected Seats
						</Text>
						{seatInfo.map((seat, seatIndex) => (
							<StyledSeatRow key={`edit_sale_seat_${seatIndex}`}>
								<Text
									$color="text_desc_alt"
									$size="1.3rem"
									$weight="400"
								>{`${seat.section} - Row ${seat.row}- Seat ${seat.seat}`}</Text>
								<ButtonRed $small={true} onClick={() => onDeleteSeat(seat.id)}>
									Remove
								</ButtonRed>
							</StyledSeatRow>
						))}
					</StyledSeatsContainer>
				</FormRow>
			)}

			<FormRow>
				<InputRow label="Secondary">
					<Select
						value={formState.secondary}
						onChange={(v) => onChange('secondary', v)}
						options={getOptionList('Secondary', convertObjectToOptions(TixSecondaryName))}
					/>
				</InputRow>
			</FormRow>

			<FormRow>
				<InputRow label="Transfer Type">
					<Select
						value={formState.transferType}
						onChange={(v) => onChange('transferType', v)}
						options={getOptionList(
							'Transer Type',
							convertEnumsToOptions(TixTransferTypes, TixTransferNames),
						)}
						disabled={isFine}
					/>
				</InputRow>
			</FormRow>

			{!isUnsold && !isFine && (
				<FormRow>
					<InputRow label="Amount (Optional)">
						<Input
							value={formState.amount?.toString() ?? ''}
							onChange={(v) => onChange('amount', v)}
							placeholder="12.34"
							onChangeMap={checkCurrencyFormatPartly}
							onBlurMap={checkCurrencyFormatFull}
						/>
					</InputRow>
					<InputRow label="Quantity">
						<Input
							value={formState.quantity?.toString() ?? ''}
							onChange={(v) => onChange('quantity', v)}
							placeholder="1"
							onChangeMap={checkIntFormatFull}
						/>
					</InputRow>
				</FormRow>
			)}

			{!isUnsold && (
				<FormRow>
					<InputRow label={`Reference ${optionalReference ? '(Optional)' : ''}`}>
						<Input
							value={formState.reference ?? ''}
							onChange={(v) => onChange('reference', v)}
							placeholder="# 123456/UK1"
						/>
					</InputRow>
				</FormRow>
			)}

			{!isFine && !isUnsold && (
				<FormRow>
					<InputRow label="Buyer Info (Optional)">
						<Input
							value={formState.buyerEmail ?? ''}
							onChange={(v) => onChange('buyerEmail', v)}
							placeholder="abc@gmail.com"
						/>
					</InputRow>
					<InputRow label="Buyer Name (Optional)">
						<Input
							value={formState.buyerName ?? ''}
							onChange={(v) => onChange('buyerName', v)}
							placeholder="John Doe"
						/>
					</InputRow>
				</FormRow>
			)}

			{!isUnsold && (
				<FormRow>
					<InputRow label="Amount Currency (Optional)">
						<Select
							value={formState.amountCurrency ?? ''}
							onChange={(v) => onChange('amountCurrency', v)}
							options={getOptionList(
								'Currency',
								convertEnumsToOptions(TixCurrencyTypes, TixCurrencyTypes),
							)}
						/>
					</InputRow>
					<InputRow label="Amount Native (Optional)">
						<Input
							value={formState.amountNative?.toString() ?? ''}
							onChange={(v) => onChange('amountNative', v)}
							placeholder="12.34"
							onChangeMap={checkCurrencyFormatPartly}
							onBlurMap={checkCurrencyFormatFull}
						/>
					</InputRow>
				</FormRow>
			)}

			<FormRow>
				<InputRow label="Note (Optional)">
					<Input
						value={formState.note ?? ''}
						onChange={(v) => onChange('note', v)}
						placeholder="Custom notes..."
					/>
				</InputRow>
			</FormRow>

			<FormRow>
				<ButtonGrey onClick={closeModal}>Cancel</ButtonGrey>
				<ButtonGreen disabled={!checkInputs() || saving} onClick={onClickSave}>
					Save
				</ButtonGreen>
			</FormRow>

			<FormRow>
				<ButtonRed onClick={onClickDeleteSale}>DELETE SALE</ButtonRed>
			</FormRow>
		</Modal>
	);
}
